import { useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";

interface TableConfig {
  visibleColumns: string[];
  /** Todas as chaves de colunas conhecidas no momento do salvamento */
  allColumnKeys?: string[];
  /** Colunas que tinham visible: false por padrão no momento do salvamento */
  hiddenByDefaultKeys?: string[];
  sortConfig: {
    key: string;
    direction: "asc" | "desc";
  } | null;
}

interface TableConfigs {
  [tableId: string]: TableConfig;
}

interface TableColumn {
  key: string;
  visible?: boolean;
  alwaysVisible?: boolean;
}

// Função para obter cookies
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(";").shift();
    return cookieValue ? decodeURIComponent(cookieValue) : null;
  }
  return null;
};

// Função para definir cookies
const setCookie = (name: string, value: string): void => {
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${name}=${encodedValue}; path=/; SameSite=Lax`;
};

// Função para gerar ID único da tabela baseado no título e colunas
const generateTableId = (
  title: string | ReactNode,
  columns: TableColumn[],
): string => {
  const titleText = typeof title === "string" ? title : String(title);
  const columnsKeys = columns
    .map((col) => col.key)
    .sort()
    .join(",");
  return `${titleText.toLowerCase().replace(/\s+/g, "-")}-${btoa(columnsKeys).slice(0, 8)}`;
};

// ===== DETECÇÃO AUTOMÁTICA DE COLUNA DE RECEITA =====
// Prioridade: total_revenue > receitaTotal > receita_total > purchase_revenue > item_revenue > revenue > receita
const REVENUE_KEY_PRIORITIES: RegExp[] = [
  /^total_revenue$/i,
  /^receitatotal$/i,
  /^receita_total$/i,
  /^purchase_revenue$/i,
  /^item_revenue$/i,
  /^revenue$/i,
  /^receita$/i,
];

// Colunas derivadas (variação, %, share) NÃO devem ser usadas como ordenação padrão
const REVENUE_EXCLUSION = /change|delta|trend|share|percent|Δ/i;

/**
 * Encontra a coluna de receita mais relevante na tabela.
 * Retorna a key da coluna ou null se não houver coluna de receita.
 */
const findRevenueColumn = (columns: TableColumn[]): string | null => {
  for (const pattern of REVENUE_KEY_PRIORITIES) {
    const match = columns.find(
      (col) => pattern.test(col.key) && !REVENUE_EXCLUSION.test(col.key),
    );
    if (match) return match.key;
  }
  return null;
};

export const useTableConfig = (
  title: string | ReactNode,
  columns: TableColumn[],
  options?: {
    defaultSortKey?: string;
    defaultSortDirection?: "asc" | "desc";
  },
) => {
  const tableId = generateTableId(title, columns);
  const defaultVisibleColumns = columns
    .filter((col) => col.visible !== false)
    .map((col) => col.key);
  const hiddenByDefaultColumns = columns
    .filter((col) => col.visible === false)
    .map((col) => col.key);

  // Colunas que devem estar sempre visíveis (não podem ser ocultadas)
  const alwaysVisibleColumns = columns
    .filter((col) => col.alwaysVisible === true)
    .map((col) => col.key);

  // Ordenação padrão: override explícito se fornecido; senão, Receita (desc)
  // se a tabela possuir coluna de receita.
  const revenueColumnKey = findRevenueColumn(columns);
  const defaultSort = options?.defaultSortKey
    ? {
        key: options.defaultSortKey,
        direction: options.defaultSortDirection ?? ("desc" as const),
      }
    : revenueColumnKey
      ? { key: revenueColumnKey, direction: "desc" as const }
      : null;

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(
    new Set(defaultVisibleColumns),
  );
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(defaultSort);

  // Carregar configuração de colunas do cookie na inicialização.
  // A ordenação NÃO é restaurada do cookie — sempre inicia com o padrão
  // (override ou Receita desc) para garantir consistência ao recarregar.
  useEffect(() => {
    const savedConfigs = getCookie("table-configs");
    if (savedConfigs) {
      try {
        const configs: TableConfigs = JSON.parse(savedConfigs);
        const tableConfig = configs[tableId];

        if (tableConfig) {
          // Cookies antigos sem allColumnKeys: resetar para defaults
          // para evitar colunas importantes ficarem escondidas.
          if (
            !tableConfig.allColumnKeys ||
            tableConfig.allColumnKeys.length === 0
          ) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setVisibleColumns(new Set(defaultVisibleColumns));
          } else {
            const savedColumns = new Set(tableConfig.visibleColumns);
            // Garantir que colunas alwaysVisible estejam sempre incluídas
            alwaysVisibleColumns.forEach((col) => savedColumns.add(col));

            const savedAllKeys = new Set(tableConfig.allColumnKeys);

            // Detectar colunas novas adicionadas após o cookie ser salvo:
            // Se a coluna é visível por padrão (visible !== false) e NÃO
            // existia quando o cookie foi salvo, incluí-la automaticamente.
            for (const col of defaultVisibleColumns) {
              if (!savedAllKeys.has(col)) {
                savedColumns.add(col);
              }
            }

            // Detectar mudanças no padrão de visibilidade:
            // Se a lista de colunas ocultas por padrão mudou desde o último
            // salvamento, resetar para os defaults (o dev mudou a definição).
            const currentHiddenByDefault = columns
              .filter((col) => col.visible === false)
              .map((col) => col.key)
              .sort();
            const savedHiddenByDefault = [
              ...(tableConfig.hiddenByDefaultKeys || []),
            ].sort();
            const hiddenDefaultsChanged =
              currentHiddenByDefault.join(",") !==
              savedHiddenByDefault.join(",");

            if (hiddenDefaultsChanged) {
              // Padrão de visibilidade mudou — resetar para defaults
              setVisibleColumns(new Set(defaultVisibleColumns));
            } else {
              setVisibleColumns(savedColumns);
            }
          }
          // sortConfig NÃO é restaurado — padrão default ao recarregar
        } else {
          // Sem cookie para este tableId — aplicar defaults das colunas atuais
          setVisibleColumns(new Set(defaultVisibleColumns));
        }
      } catch (error) {
        console.warn("Erro ao carregar configurações da tabela:", error);
        setVisibleColumns(new Set(defaultVisibleColumns));
      }
    } else {
      // Sem cookie nenhum — aplicar defaults das colunas atuais
      setVisibleColumns(new Set(defaultVisibleColumns));
    }
    // Resetar ordenação para o padrão ao trocar de tabela
    setSortConfig(defaultSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  // Salvar configuração de colunas no cookie sempre que houver mudanças.
  // A ordenação NÃO é persistida — sempre reseta ao padrão ao recarregar.
  const saveConfig = useCallback(() => {
    const savedConfigs = getCookie("table-configs");
    let configs: TableConfigs = {};

    if (savedConfigs) {
      try {
        configs = JSON.parse(savedConfigs);
      } catch (error) {
        console.warn("Erro ao parsear configurações existentes:", error);
      }
    }

    configs[tableId] = {
      visibleColumns: Array.from(visibleColumns),
      allColumnKeys: columns.map((col) => col.key),
      hiddenByDefaultKeys: hiddenByDefaultColumns,
      sortConfig: null, // Não persiste ordenação — padrão default ao recarregar
    };

    setCookie("table-configs", JSON.stringify(configs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, visibleColumns]);

  // Salvar configuração sempre que houver mudanças de colunas
  useEffect(() => {
    saveConfig();
  }, [saveConfig]);

  // Função para alternar visibilidade de coluna
  const toggleColumnVisibility = useCallback(
    (columnKey: string) => {
      // Não permitir ocultar colunas marcadas como alwaysVisible
      if (alwaysVisibleColumns.includes(columnKey)) {
        return;
      }

      setVisibleColumns((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(columnKey)) {
          newSet.delete(columnKey);
        } else {
          newSet.add(columnKey);
        }
        return newSet;
      });
    },
    [alwaysVisibleColumns],
  );

  // Função para mostrar/ocultar todas as colunas
  const toggleAllColumns = useCallback(
    (show: boolean) => {
      if (show) {
        setVisibleColumns(new Set(columns.map((col) => col.key)));
      } else {
        setVisibleColumns(new Set());
      }
    },
    [columns],
  );

  // Função para definir ordenação
  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.direction === "asc" ? { key, direction: "desc" } : null;
      }
      return { key, direction: "asc" };
    });
  }, []);

  return {
    visibleColumns,
    sortConfig,
    toggleColumnVisibility,
    toggleAllColumns,
    handleSort,
    tableId,
  };
};
